import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { FiExternalLink, FiPlus, FiCheck, FiX } from 'react-icons/fi';
import {
  listDwAgents, listDwAgentActivity, getAgentRoutineRuns,
  type DwAgentRecord, type DwAgentActivity,
} from '../../features/claudeagents/claudeAgentApi';
import { DW_CONFIGURED_EVENT } from '../../features/digitalworkforce/dwAutoSetup';
import { subscribeToKanbanUpdates } from '../../services/communicationService';
import { getDigitalTeamMembers, setDigitalTeamMembers } from '../../services/userPreferencesService';
import { useActiveAgent } from '../../context/ActiveAgentContext';
import { colorForAgent, initialsForAgent, displayNameForAgent } from '../../utils/agentAvatar';
import { agentAvatarFor, useAgentPhotosVersion } from '../../utils/agentPhotos';
import { ROUTES } from '../../app/routes';
import '../../styles/SidebarDigitalTeam.css';

/**
 * "Digital Team" — the agents you've chosen to keep an eye on, not the
 * whole roster. It starts empty with just "Add"; the picker lists every
 * agent (Momentum's built-in agents and your own digital agents) and you
 * tick the few you care about, so the sidebar never turns into a long
 * scroll of agents you don't work with.
 *
 * Each member shows a live status, social-feed style — a glowing ring on
 * the avatar while they're active, and a status line saying what they're
 * doing. Every state comes from real data, never a fake "typing…":
 *   - Replying to you — a chat surface (docked bar or full chat) is
 *                       streaming their reply right now.
 *   - Running routine — one of their routine runs has status "running".
 *   - Working on      — they have a Kanban command in "in progress" (the
 *                       same feed Command Center uses); the title is shown.
 *   - Queued          — open commands, none started yet.
 *   - Chatted with you — their last reply to you finished in the past
 *                       15 minutes ("· 3m ago").
 *   - Available       — nothing open.
 * Routine runs are only fetched for members (so adding few agents keeps
 * this cheap) and refreshed every minute; Kanban activity refreshes live.
 *
 * Membership is saved per user in user preferences (like pinned and hidden
 * menu items); localStorage is only a cache so it shows before that loads.
 */

const STORAGE_KEY = 'ig-digital-team-members';
const ROUTINE_POLL_MS = 60_000;
const RECENT_CHAT_MS = 15 * 60_000;

function ago(ms: number): string {
  const mins = Math.floor(ms / 60_000);
  return mins < 1 ? 'just now' : `${mins}m ago`;
}

type Presence = 'chatting' | 'routine' | 'working' | 'queued' | 'recent' | 'available';

interface MemberStatus {
  presence: Presence;
  label: string;
}

function readMembers(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? raw.split(',').filter(Boolean) : [];
  } catch { return []; }
}

function isMomentumAgent(a: DwAgentRecord): boolean {
  return !!(a.is_system || a.is_premade);
}

interface Props {
  collapsed: boolean;
}

const SidebarDigitalTeam: React.FC<Props> = ({ collapsed }) => {
  const navigate = useNavigate();
  const { setActiveAgent, replyingAgents, lastChatAt } = useActiveAgent();
  useAgentPhotosVersion();
  // Re-render every 30s so "Chatted with you · 3m ago" stays current and
  // drops off on time.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(t);
  }, []);
  const [agents, setAgents] = useState<DwAgentRecord[] | null>(null);
  const [activity, setActivity] = useState<Record<string, DwAgentActivity>>({});
  const [runningRoutine, setRunningRoutine] = useState<Record<string, string>>({});
  const [memberIds, setMemberIds] = useState<string[]>(readMembers);
  useEffect(() => {
    getDigitalTeamMembers().then((ids) => {
      if (!ids) return;
      setMemberIds(ids);
      try { localStorage.setItem(STORAGE_KEY, ids.join(',')); } catch {}
    });
  }, []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const addBtnRef = useRef<HTMLButtonElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  const loadActivity = useCallback(() => {
    listDwAgentActivity().then(setActivity).catch(() => {});
  }, []);

  useEffect(() => {
    // A failed roster fetch and "no agents configured" are handled
    // identically on purpose — both mean there's nothing honest to show,
    // and this section should just not appear rather than error.
    const loadRoster = () => { listDwAgents(false, true).then(setAgents).catch(() => setAgents([])); };
    loadRoster();
    loadActivity();
    const unsubscribe = subscribeToKanbanUpdates(loadActivity);
    // A first-time user's roster is empty until the background setup started
    // at sign-in finishes - load it again when it does.
    window.addEventListener(DW_CONFIGURED_EVENT, loadRoster);
    return () => {
      unsubscribe();
      window.removeEventListener(DW_CONFIGURED_EVENT, loadRoster);
    };
  }, [loadActivity]);

  const roster = useMemo(() => (agents ?? []).filter(a => !a.is_archived), [agents]);
  const members = useMemo(
    () => memberIds.map(id => roster.find(a => a.agent_id === id)).filter((a): a is DwAgentRecord => !!a),
    [memberIds, roster],
  );

  // Routine runs, members only.
  const memberKey = members.map(m => m.agent_id).join(',');
  useEffect(() => {
    if (members.length === 0) { setRunningRoutine({}); return; }
    let cancelled = false;
    const load = () => {
      Promise.all(members.map(m =>
        getAgentRoutineRuns(m.agent_id, m.name, 20)
          .then(runs => {
            const live = runs.find(r => (r.status ?? '').toLowerCase() === 'running');
            return [m.agent_id, live ? (live.name || 'a routine') : ''] as const;
          })
          .catch(() => [m.agent_id, ''] as const),
      )).then(pairs => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const [id, name] of pairs) if (name) next[id] = name;
        setRunningRoutine(next);
      });
    };
    load();
    const timer = window.setInterval(load, ROUTINE_POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
    // memberKey stands in for `members` — same ids, stable string identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberKey]);

  const statusFor = useCallback((agentId: string): MemberStatus => {
    const routine = runningRoutine[agentId];
    const { openCount = 0, currentTask = null } = activity[agentId] ?? {};
    const lastChat = lastChatAt[agentId];
    if (replyingAgents[agentId]) return { presence: 'chatting', label: 'Replying to you…' };
    if (routine) return { presence: 'routine', label: `Running routine: ${routine}` };
    if (currentTask) return { presence: 'working', label: `Working on: ${currentTask}` };
    if (openCount > 0) return { presence: 'queued', label: `${openCount} command${openCount === 1 ? '' : 's'} queued` };
    if (lastChat && now - lastChat < RECENT_CHAT_MS) {
      return { presence: 'recent', label: `Chatted with you · ${ago(now - lastChat)}` };
    }
    return { presence: 'available', label: 'Available' };
  }, [activity, runningRoutine, replyingAgents, lastChatAt, now]);

  const saveMembers = useCallback((next: string[]) => {
    setMemberIds(next);
    try { localStorage.setItem(STORAGE_KEY, next.join(',')); } catch {}
    void setDigitalTeamMembers(next);
  }, []);

  const toggleMember = useCallback((agentId: string) => {
    saveMembers(memberIds.includes(agentId) ? memberIds.filter(id => id !== agentId) : [...memberIds, agentId]);
  }, [memberIds, saveMembers]);

  // Drag (or Alt+↑/↓) to rearrange; the saved order is the display order.
  const moveMember = useCallback((agentId: string, toIndex: number) => {
    if (!memberIds.includes(agentId)) return;
    const next = memberIds.filter(id => id !== agentId);
    next.splice(Math.max(0, Math.min(toIndex, next.length)), 0, agentId);
    if (next.join(',') === memberIds.join(',')) return;
    saveMembers(next);
  }, [memberIds, saveMembers]);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const openPicker = () => {
    const rect = addBtnRef.current?.getBoundingClientRect();
    if (rect) {
      // Opens beside the rail when collapsed, under the button otherwise,
      // and never taller than the space left below it.
      const top = collapsed ? rect.top : rect.bottom + 6;
      setPickerPos({
        top,
        left: collapsed ? rect.right + 8 : Math.max(8, rect.left),
        maxHeight: Math.max(220, window.innerHeight - top - 16),
      });
    }
    setPickerOpen(o => !o);
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (pickerRef.current?.contains(t) || addBtnRef.current?.contains(t)) return;
      setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setPickerOpen(false); addBtnRef.current?.focus(); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pickerOpen]);

  if (roster.length === 0) return null;

  const visualFor = (agent: DwAgentRecord) => {
    const name = displayNameForAgent(agent.name, agent.is_system);
    return { name, photo: agentAvatarFor(agent.agent_id, agent.is_system ? name : undefined) };
  };

  const openAgent = (agent: DwAgentRecord) => {
    const { name, photo } = visualFor(agent);
    setActiveAgent({
      id: agent.agent_id,
      name,
      avatarColor: colorForAgent(agent.agent_id),
      avatarInitials: initialsForAgent(name),
      avatarPhoto: photo,
    });
    navigate(ROUTES.DIGITAL_WORKFORCE, { state: { selectAgentId: agent.agent_id } });
  };
  const openHomepage = (e: React.MouseEvent) => {
    e.stopPropagation();
    // A plain navigate() to the already-active route is a no-op in React
    // Router — it wouldn't reset a currently-open agent's detail view back
    // to the roster. The nonce forces a fresh location.state every click,
    // which DigitalWorkforcePage always acts on, so this button truly
    // always lands on the homepage, not just "the same URL."
    navigate(ROUTES.DIGITAL_WORKFORCE, { state: { resetToHome: Date.now() } });
  };

  const avatar = (agent: DwAgentRecord, size: 'md' | 'sm' = 'md') => {
    const { name, photo } = visualFor(agent);
    const cls = `sidebar-digital-team__avatar sidebar-digital-team__avatar--${size}`;
    return photo
      ? <img src={photo} alt="" className={`${cls} sidebar-digital-team__avatar--photo`} />
      : <span className={cls} style={{ background: colorForAgent(agent.agent_id) }}>{initialsForAgent(name)}</span>;
  };

  const momentumAgents = roster.filter(isMomentumAgent);
  const ownAgents = roster.filter(a => !isMomentumAgent(a));

  const pickerGroup = (title: string, list: DwAgentRecord[]) => list.length > 0 && (
    <div className="sidebar-digital-team__picker-group" role="group" aria-label={title}>
      <div className="sidebar-digital-team__picker-group-title">{title}</div>
      {list.map(agent => {
        const on = memberIds.includes(agent.agent_id);
        const status = statusFor(agent.agent_id);
        return (
          <button
            key={agent.agent_id}
            type="button"
            role="menuitemcheckbox"
            aria-checked={on}
            className={`sidebar-digital-team__picker-item${on ? ' sidebar-digital-team__picker-item--on' : ''}`}
            onClick={() => toggleMember(agent.agent_id)}
          >
            <span className={`sidebar-digital-team__ring sidebar-digital-team__ring--${status.presence}`}>
              {avatar(agent, 'sm')}
            </span>
            <span className="sidebar-digital-team__picker-text">
              <span className="sidebar-digital-team__picker-name">{visualFor(agent).name}</span>
              <span className="sidebar-digital-team__picker-status">{status.label}</span>
            </span>
            <span className="sidebar-digital-team__picker-check" aria-hidden="true">
              {on && <FiCheck size={12} />}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="nav-section sidebar-digital-team">
      {!collapsed && (
        <div className="sidebar-digital-team__header">
          <span className="sidebar-digital-team__header-label">Digital Team</span>
          <span className="sidebar-digital-team__header-actions">
            {members.length > 0 && (
              <button
                ref={addBtnRef}
                type="button"
                className="sidebar-digital-team__icon-btn"
                onClick={openPicker}
                title="Add or remove teammates"
                aria-label="Add or remove teammates"
                aria-haspopup="menu"
                aria-expanded={pickerOpen}
              >
                <FiPlus size={13} />
              </button>
            )}
            <button
              type="button"
              className="sidebar-digital-team__icon-btn"
              onClick={openHomepage}
              title="Open Digital Workforce — see what every agent is doing"
              aria-label="Open Digital Workforce"
            >
              <FiExternalLink size={12} />
            </button>
          </span>
        </div>
      )}

      <div className={`sidebar-digital-team__list${collapsed ? ' sidebar-digital-team__list--collapsed' : ''}`}>
        {members.map((agent, index) => {
          const status = statusFor(agent.agent_id);
          const idIndex = memberIds.indexOf(agent.agent_id);
          const dropBefore = draggingId !== null && draggingId !== agent.agent_id && dropIndex === idIndex;
          const dropAfter = draggingId !== null && draggingId !== agent.agent_id && dropIndex === idIndex + 1 && index === members.length - 1;
          const active = status.presence === 'chatting' || status.presence === 'routine' || status.presence === 'working';
          const { name } = visualFor(agent);
          return (
            <div
              key={agent.agent_id}
              className={`sidebar-digital-team__row${active ? ' sidebar-digital-team__row--active' : ''}${draggingId === agent.agent_id ? ' sidebar-digital-team__row--dragging' : ''}${dropBefore ? ' sidebar-digital-team__row--drop-before' : ''}${dropAfter ? ' sidebar-digital-team__row--drop-after' : ''}`}
              role="button"
              tabIndex={0}
              draggable
              aria-label={`${name} — ${status.label}`}
              aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
              title={collapsed ? `${name} — ${status.label}` : undefined}
              onClick={() => openAgent(agent)}
              onKeyDown={(e) => {
                if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                  e.preventDefault();
                  moveMember(agent.agent_id, idIndex + (e.key === 'ArrowDown' ? 1 : -1));
                  return;
                }
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAgent(agent); }
              }}
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', agent.agent_id);
                setDraggingId(agent.agent_id);
              }}
              onDragOver={(e) => {
                if (!draggingId) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const rect = e.currentTarget.getBoundingClientRect();
                setDropIndex(idIndex + (e.clientY > rect.top + rect.height / 2 ? 1 : 0));
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (draggingId && dropIndex !== null) {
                  const from = memberIds.indexOf(draggingId);
                  moveMember(draggingId, from < dropIndex ? dropIndex - 1 : dropIndex);
                }
                setDraggingId(null);
                setDropIndex(null);
              }}
              onDragEnd={() => { setDraggingId(null); setDropIndex(null); }}
            >
              <span className={`sidebar-digital-team__ring sidebar-digital-team__ring--${status.presence}`}>
                {avatar(agent)}
              </span>
              {!collapsed && (
                <span className="sidebar-digital-team__info">
                  <span className="sidebar-digital-team__name">{name}</span>
                  <span className={`sidebar-digital-team__status sidebar-digital-team__status--${status.presence}`}>
                    {(active || status.presence === 'available') && <span className="sidebar-digital-team__live" aria-hidden="true" />}
                    <span className="sidebar-digital-team__status-text">{status.label}</span>
                  </span>
                </span>
              )}
            </div>
          );
        })}

        {/* Empty team (or the collapsed rail): the Add control is the row. */}
        {(members.length === 0 || collapsed) && (
          <button
            ref={addBtnRef}
            type="button"
            className={`sidebar-digital-team__add${collapsed ? ' sidebar-digital-team__add--rail' : ''}`}
            onClick={openPicker}
            title={collapsed ? 'Add teammates' : undefined}
            aria-label="Add teammates"
            aria-haspopup="menu"
            aria-expanded={pickerOpen}
          >
            <span className="sidebar-digital-team__add-icon" aria-hidden="true"><FiPlus size={14} /></span>
            {!collapsed && <span>Add</span>}
          </button>
        )}
      </div>

      {pickerOpen && pickerPos && createPortal(
        <div
          ref={pickerRef}
          className="sidebar-digital-team__picker"
          role="menu"
          aria-label="Choose your Digital Team"
          style={{ top: pickerPos.top, left: pickerPos.left, maxHeight: pickerPos.maxHeight }}
        >
          <div className="sidebar-digital-team__picker-head">
            <span>Your Digital Team</span>
            <button
              type="button"
              className="sidebar-digital-team__icon-btn"
              onClick={() => setPickerOpen(false)}
              aria-label="Close"
            >
              <FiX size={13} />
            </button>
          </div>
          <div className="sidebar-digital-team__picker-body">
            {pickerGroup('Momentum agents', momentumAgents)}
            {pickerGroup('Your digital agents', ownAgents)}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default SidebarDigitalTeam;
